import { addMessages, getLocaleFromNavigator, init, locale } from 'svelte-i18n';
import { get } from 'svelte/store';

import cs from '../i18n/cs.json';
import en from '../i18n/en.json';
import ar from '../i18n/ar.json';

export const SUPPORTED_LOCALES = ['cs', 'en', 'ar'] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: AppLocale = 'cs';

let initialized = false;

const normalizeLocale = (value?: string | null): AppLocale => {
  if (!value) return DEFAULT_LOCALE;
  const normalized = value.toLowerCase().split('-')[0] as AppLocale;
  return SUPPORTED_LOCALES.includes(normalized) ? normalized : DEFAULT_LOCALE;
};

const detectInitialLocale = (preferredLocale?: string | null): AppLocale => {
  if (preferredLocale) return normalizeLocale(preferredLocale);
  const navigatorLocale = typeof window !== 'undefined' ? getLocaleFromNavigator() : null;
  return normalizeLocale(navigatorLocale);
};

export const setupI18n = (preferredLocale?: string | null) => {
  const initialLocale = detectInitialLocale(preferredLocale);

  if (!initialized) {
    addMessages('cs', cs);
    addMessages('en', en);
    addMessages('ar', ar);

    init({
      fallbackLocale: DEFAULT_LOCALE,
      initialLocale,
    });

    initialized = true;
  } else {
    setAppLocale(initialLocale);
  }
};

export const setAppLocale = (nextLocale?: string | null): AppLocale => {
  const resolved = normalizeLocale(nextLocale);
  locale.set(resolved);
  return resolved;
};

export const getCurrentLanguage = (): AppLocale => {
  const current = get(locale) as AppLocale | undefined;
  return current && SUPPORTED_LOCALES.includes(current) ? current : DEFAULT_LOCALE;
};

export const isRTL = (value?: string | null): boolean => {
  const resolved = value ? normalizeLocale(value) : getCurrentLanguage();
  return resolved === 'ar';
};
